import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { DeviceService } from '../../../../app/modules/devices/services/device.service';
import {
  BuildingSettingModel,
  DeviceDashboardModel,
  DeviceManagementPayload,
  DeviceManagementResponse,
  DeviceSettingModel,
  DeviceTypes,
  GetDeviceTypeModel,
  UpdateCommandPayload,
  updateSetPointPayload,
} from '../../dashboard/models/dashboard.model';

export interface allDeviceData {
  buildingId?: string;
  thermostat?: any[];
  temperature?: any[];
  energy?: any[];
  lighting?: any[];
  refrigeration?: any[];
}

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  private apiUrl = `${environment.apiURL}/dashboard`;
  private apideviceUrl = `${environment.apiURL}/device`;
  private apiLocationUrl = `${environment.apiURL}/location`;
  private apiManagementUrl = `${environment.apiURL}/devicemanagementview`;

  private allDeviceDataSource = new BehaviorSubject<allDeviceData>({});
  allDeviceData$ = this.allDeviceDataSource.asObservable();

  // Emits the device type and view context to refresh
  private refreshTriggerSource = new Subject<{ type: string; isSummaryView: boolean }>();
  refreshTrigger$ = this.refreshTriggerSource.asObservable();

  triggerRefresh(deviceType: string, isSummaryView: boolean): void {
    this.refreshTriggerSource.next({ type: deviceType, isSummaryView });
  }

  // Filter state management
  private filteredBuildingsSubject = new BehaviorSubject<any[]>([]);
  public filteredBuildings$: Observable<any[]> = this.filteredBuildingsSubject.asObservable();

  // Device location for header display - stores address components and logo
  private deviceLocationSubject = new BehaviorSubject<{
    storeNumber?: string;
    streetAddress?: string;
    cityStateZip?: string;
    locationLogo?: string;
  }>({});
  public deviceLocation$: Observable<{ storeNumber?: string; streetAddress?: string; cityStateZip?: string; locationLogo?: string; }> = this.deviceLocationSubject.asObservable();


  constructor(private http: HttpClient,
    private deviceService: DeviceService
  ) {
    const stored = localStorage.getItem('filteredDevices');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        this.filteredBuildingsSubject.next(parsed);
      } catch (error) {
        console.error('Error parsing filteredDevices from localStorage', error);
      }
    }
  }

  get baseApiUrl(): string {
    return this.apiUrl;
  }

  private loadingMap = new BehaviorSubject<{ [key: string]: boolean }>({});

  loading$ = this.loadingMap.asObservable();

  setLoading(key: string, value: boolean) {
    const current = this.loadingMap.value;
    this.loadingMap.next({ ...current, [key]: value });
  }

  get currentDeviceData(): allDeviceData {
    return this.allDeviceDataSource.value;
  }

  updateDeviceData(partial: Partial<allDeviceData>) {
    const current = this.allDeviceDataSource.value;
    this.allDeviceDataSource.next({ ...current, ...partial });
  }

  // Get Device Types By Building
  getDeviceTypesByBuilding(getDeviceTypeModel: GetDeviceTypeModel): Observable<DeviceTypes[]> {
    return this.http.post<DeviceTypes[]>(`${this.apiUrl}/deviceTypesByBuilding`, getDeviceTypeModel);
  }

  getThermostateData(deviceDashboardModel: DeviceDashboardModel, skipLoader = false): Observable<any[]> {
    const { includeZigbee, ...body } = deviceDashboardModel;
    let params = new HttpParams();
    if (includeZigbee !== undefined) {
      params = params.set('includeZigbee', String(includeZigbee));
    }
    const headers = skipLoader ? new HttpHeaders({ 'X-Skip-Loader': 'true' }) : new HttpHeaders();
    return this.http.post<any[]>(`${this.apiUrl}/devices/thermostate`, body, { params, headers });
  }
  getLightingData(deviceDashboardModel: DeviceDashboardModel): Observable<any[]> {
    const headers = new HttpHeaders({ 'X-Skip-Loader': 'true' });
    return this.http.post<any[]>(`${this.apiUrl}/devices/lighting`, deviceDashboardModel, { headers });
  }
  getLightingZonesData(buildingId: string, deviceTypeIds: string[]): Observable<any> {
    const headers = new HttpHeaders({ 'X-Skip-Loader': 'true' });
    return this.http.post<any>(`${this.apiUrl}/devices/lighting/zones`, { buildingId, deviceTypeId: deviceTypeIds }, { headers });
  }
  getRefrigerationData(deviceDashboardModel: DeviceDashboardModel): Observable<any[]> {
    const headers = new HttpHeaders({ 'X-Skip-Loader': 'true' });
    return this.http.post<any[]>(`${this.apiUrl}/devices/refrigeration`, deviceDashboardModel, { headers });
  }
  getTemperatureData(deviceDashboardModel: DeviceDashboardModel): Observable<any[]> {
    const headers = new HttpHeaders({ 'X-Skip-Loader': 'true' });
    return this.http.post<any[]>(`${this.apiUrl}/devices/temperature`, deviceDashboardModel, { headers });
  }

  UpdateThermostate(deviceSettingModel: DeviceSettingModel, deviceId: string): Observable<any[]> {
    return this.http.put<any[]>(`${this.apideviceUrl}/DeviceSetting/${deviceId}`, deviceSettingModel);
  }

  clearDashboardData() {
    this.allDeviceDataSource.next({});
  }

  UpdateBuildingEnergySetting(energySettingModel: BuildingSettingModel, buildingId: string): Observable<any[]> {
    return this.http.put<any[]>(`${this.apiLocationUrl}/building/${buildingId}/setting`, energySettingModel);
  }
  updateLepidoSetting(energySettingModel: BuildingSettingModel, buildingId: string): Observable<any[]> {
    return this.http.put<any[]>(`${this.apiLocationUrl}/building/${buildingId}/setting`, energySettingModel);
  }

  getBuildingEnergySetting(buildingId: string): Observable<BuildingSettingModel> {
    return this.http.get<BuildingSettingModel>(`${this.apiLocationUrl}/building/${buildingId}/setting`);
  }
  getLepidoSetting(buildingId: string): Observable<BuildingSettingModel> {
    return this.http.get<BuildingSettingModel>(`${this.apiLocationUrl}/building/${buildingId}/setting`);
  }

  /**
   * Set filtered devices
   */
  setFilteredDevices(devices: any[]): void {
    // Keep only required properties
    const filteredData = devices.map(device => ({
      address1: device.address1,
      address2: device.address2,
      buildingId: device.buildingId,
      floorId: device.floorId,
      locationId: device.locationId,
      departmentId: device.departmentId,
      customerId: device.customerId,
      departmentName: device.departmentName
    }));

    // Update BehaviorSubject (if needed)
    this.filteredBuildingsSubject.next(filteredData);
    localStorage.setItem('filteredDevices', JSON.stringify(filteredData));
  }

  /**
   * Get current filtered buildings value
   */
  getFilteredDevices(): any[] {
    return this.filteredBuildingsSubject.getValue();
  }

  setLocationName(buildingId: string): void {

    const payload = {
      buildingIds: [buildingId],
      pageNumber: 1,
      pageSize: 10
    } as any;

    this.deviceService.searchDevices(payload).subscribe({
      next: (response) => {

        if (response?.devices?.length) {

          const device = response.devices[0] as any;
          const {
            address1,
            address2,
            city,
            zipCode,
            country,
            locationName,
            brandLogo
          } = device;
          // Use locationName as store number
          const storeNumber = locationName || '';

          // Build street address (address1, address2)
          const streetAddressParts = [address1, address2].filter(Boolean);
          const streetAddress = streetAddressParts.join(', ');

          // Build city, country, zip line
          const cityStateZipParts = [city, country, zipCode].filter(Boolean);
          const cityStateZip = cityStateZipParts.join(', ');

          // Send location to BehaviorSubject with separated address components and logo
          this.deviceLocationSubject.next({
            storeNumber: storeNumber || '',
            streetAddress: streetAddress,
            cityStateZip: cityStateZip,
            locationLogo: brandLogo || ''
          });

        } else {
          console.warn('No devices found in response');
        }

      },
      error: (error) => {
        console.error('API Error:', error);
      }
    });

  }

  getDeviceManagementViewList(
    payload: DeviceManagementPayload
  ): Observable<DeviceManagementResponse> {

    // Optional safety check (very useful in debugging)
    if (!payload.startDate || !payload.endDate) {
      console.warn('StartDate or EndDate missing in payload:', payload);
    }

    return this.http.post<DeviceManagementResponse>(
      `${this.apiManagementUrl}/search`,
      payload
    );
  }

  getHeatReclamationChart(deviceId: string, startDate: Date, endDate: Date): Observable<any> {
    return this.http.post<any>(`${this.apiManagementUrl}/heatReclamationChart`, {
      deviceId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });
  }

  updateCommand(
    payload: UpdateCommandPayload
  ): Observable<DeviceManagementResponse> {

    return this.http.post<DeviceManagementResponse>(
      `${this.apiManagementUrl}/send-command`,
      payload
    );
  }

  updateSetPoint(
    deviceId: string,
    payload: updateSetPointPayload
  ): Observable<any> {

    return this.http.post<any>(
      `${this.apideviceUrl}/setPoint/${deviceId}`,
      payload
    );
  }

}